import { useState, useEffect } from "react";
import { useTaskCoach, isTooVague } from "@/hooks/useTaskCoach";
import { useDeviceHashes } from "@/hooks/useDeviceHashes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";

interface ChildAddDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (title: string, durationMinutes: number) => void;
  isPending: boolean;
}

export function ChildAddDrawer({ open, onOpenChange, onAdd, isPending }: ChildAddDrawerProps) {
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(10);
  const deviceHashes = useDeviceHashes();
  const { coach, isLoading: coachLoading, check, reset, quotaExceeded, quotaUsed, quotaLimit, coachingDisabled } = useTaskCoach(deviceHashes);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDuration(10);
      reset();
    }
  }, [open]);

  function handleTitleChange(val: string) {
    setTitle(val);
    check(val);
  }

  function applySuggestion(suggestion: string) {
    // Extract duration like "20 min", "15 minutter" from suggestion
    const timeMatch = suggestion.match(/\b(\d+)\s*(?:min(?:utter|utt)?)\b/i);
    let cleanTitle = suggestion;
    if (timeMatch) {
      const minutes = parseInt(timeMatch[1], 10);
      // Snap to nearest valid slider step (5–30, step 5)
      const snapped = Math.min(60, Math.max(5, Math.round(minutes / 5) * 5));
      setDuration(snapped);
      // Remove the time phrase (and surrounding "i " or trailing whitespace)
      cleanTitle = suggestion
        .replace(/\s*i\s+\d+\s*(?:min(?:utter|utt)?)\b/i, "")
        .replace(/\s*\d+\s*(?:min(?:utter|utt)?)\b/i, "")
        .trim();
    }
    setTitle(cleanTitle);
    reset();
  }

  const vague = isTooVague(title);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="min-h-[420px] max-h-[92vh]">
        <DrawerHeader className="flex-shrink-0">
          <DrawerTitle>Legg til noe du skal gjøre</DrawerTitle>
          <DrawerDescription>Skriv hva du skal gjøre i dag</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <Input
              placeholder="F.eks. Lese 10 sider"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="text-base"
              
            />
            {/* Quota exceeded */}
            {quotaExceeded && !coachingDisabled && vague && title.trim().length >= 2 && (
              <div className="mt-2 rounded-lg border border-border bg-muted/30 p-2.5">
                <p className="text-xs text-muted-foreground">
                  ✨ AI-forslagene er brukt opp ({quotaUsed}/{quotaLimit}). Tips: gjør oppgaven mer konkret selv 😊
                </p>
              </div>
            )}
            {/* Vague hint */}
            {!quotaExceeded && !coachingDisabled && vague && !coach && !coachLoading && title.trim().length >= 2 && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Tips: gjør den konkret 😊
              </p>
            )}
            {/* Loading */}
            {coachLoading && (
              <p className="text-xs text-muted-foreground mt-1.5 animate-pulse">
                Tenker på forslag …
              </p>
            )}
            {/* Coach suggestions */}
            {coach && coach.rewriteSuggestions.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {coach.hint && (
                  <p className="text-xs text-muted-foreground">{coach.hint}</p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {coach.rewriteSuggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      onClick={() => applySuggestion(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {quotaUsed !== undefined && quotaLimit !== undefined && (
                  <p className="text-[10px] text-muted-foreground/60">
                    AI-forslag {quotaUsed}/{quotaLimit}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Duration: quick picks + slider */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Tid: {duration} min
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {[5, 10, 15, 20, 30, 45, 60].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setDuration(value)}
                  className={`text-sm rounded-full px-3.5 py-1.5 transition-transform active:scale-95 ${
                    duration === value
                      ? "bg-primary/10 text-primary ring-1 ring-primary/20"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  {value} min
                </button>
              ))}
            </div>
            <div className="py-2">
              <Slider
                min={5}
                max={60}
                step={5}
                value={[duration]}
                onValueChange={([v]) => setDuration(v)}
              />
            </div>
          </div>
        </div>
        <DrawerFooter className="flex-shrink-0">
          <Button
            onClick={() => onAdd(title.trim(), duration)}
            disabled={!title.trim() || isPending}
            className="w-full"
          >
            {isPending ? "Legger til …" : "Legg til ✅"}
          </Button>
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">Avbryt</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
